import { toastrDismiss } from '@store/toolkit/toastr/toastrActions.js';
import { selectToastrItems } from '@store/toolkit/toastr/toastrSelectors.js';
import type { LucideIcon } from 'lucide-react';
import { Check, Info, TriangleAlert, X } from 'lucide-react';
import type { PanInfo } from 'motion/react';
import { AnimatePresence } from 'motion/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { ToastType } from './toastr.styled';
import * as S from './toastr.styled';

type ToastrItem = { id: number; message: string; type: ToastType };

// Errors stay up longer than confirmations — they have to be read, not just noticed.
const DURATION: Record<ToastType, number> = { success: 3500, error: 6000, info: 4500 };

const ICON: Record<ToastType, LucideIcon> = { success: Check, error: TriangleAlert, info: Info };

const SWIPE_DISMISS_PX = 90;

const Toast = ({ item }: { item: ToastrItem }) => {
  const dispatch = useDispatch();

  const [isPaused, setIsPaused] = useState(false);
  // Hovering pauses the countdown, so what is left of it has to survive the effect re-run.
  const remainingRef = useRef(DURATION[item.type]);

  const handleDismiss = useCallback(() => dispatch(toastrDismiss(item.id)), [dispatch, item.id]);

  useEffect(() => {
    if (isPaused) return undefined;

    const startedAt = Date.now();
    const timer = window.setTimeout(handleDismiss, remainingRef.current);

    return () => {
      window.clearTimeout(timer);
      remainingRef.current = Math.max(0, remainingRef.current - (Date.now() - startedAt));
    };
  }, [isPaused, handleDismiss]);

  const handleDragEnd = (_event: unknown, info: PanInfo) => {
    if (Math.abs(info.offset.x) > SWIPE_DISMISS_PX) handleDismiss();
  };

  const Icon = ICON[item.type];

  return (
    <S.Toast
      data-type={item.type}
      layout
      drag='x'
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.4}
      onDragEnd={handleDragEnd}
      onHoverStart={() => setIsPaused(true)}
      onHoverEnd={() => setIsPaused(false)}
      initial={{ opacity: 0, y: 20, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 8, scale: 0.96, transition: { duration: 0.18 } }}
      transition={{ type: 'spring', stiffness: 420, damping: 32 }}
    >
      <S.ToastIcon>
        <Icon />
      </S.ToastIcon>
      <S.ToastMessage>{item.message}</S.ToastMessage>
      <S.ToastClose type='button' aria-label='Close' onClick={handleDismiss}>
        <X />
      </S.ToastClose>
      <S.ToastProgress $duration={DURATION[item.type]} $isPaused={isPaused} />
    </S.Toast>
  );
};

export const Toastr = () => {
  const items: ToastrItem[] = useSelector(selectToastrItems);

  return (
    <S.ToastrContainer aria-live='polite'>
      <AnimatePresence initial={false}>
        {items.map((item) => (
          <Toast key={item.id} item={item} />
        ))}
      </AnimatePresence>
    </S.ToastrContainer>
  );
};
